/**
 * Shared types для системы контроля доступа по подписке
 * Используются как на frontend, так и на backend
 */

export type SubscriptionTier = 'free' | 'base' | 'premium' | 'pro';

export type BillingPeriod = 'monthly' | 'yearly';

export interface FeatureAccessMap {
  [featureId: string]: FeatureAccessInfo;
}

export interface FeatureAccessInfo {
  name: string;
  requiredTier: SubscriptionTier;
  description?: string;
  category?: 'forecasts' | 'compatibility' | 'numerology' | 'astrology' | 'special';
}

export interface AccessCheckResult {
  hasAccess: boolean;
  requiredTier: SubscriptionTier;
  userTier: SubscriptionTier;
  reason?: 'no_subscription' | 'insufficient_tier' | 'expired';
  daysLeft?: number;
  endDate?: string;
}

export interface SubscriptionStatus {
  tier: SubscriptionTier;
  billingPeriod: BillingPeriod;
  endDate: string;
  isActive: boolean;
  daysLeft: number;
  autoRenew: boolean;
}

export interface ContactLimits {
  canAdd: boolean;
  limit: number;
  remaining: number;
}

// Порядок тарифов (для сравнения уровней)
export const TIER_ORDER: Record<SubscriptionTier, number> = {
  free: 0,
  base: 1,
  premium: 2,
  pro: 3,
};

// Цвета бейджей для тарифов
export const TIER_BADGE_COLORS: Record<SubscriptionTier, string> = {
  free: '#9ca3af',    // серый
  base: '#4fd1c5',    // голубой
  premium: '#d4af37', // золотой
  pro: '#9B59B6',     // фиолетовый
};

// Названия тарифов для отображения
export const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  base: 'Base',
  premium: 'Premium',
  pro: 'Pro',
};

// Цены тарифов (месяц/год)
export const TIER_PRICES: Record<SubscriptionTier, { monthly: number; yearly: number }> = {
  free: { monthly: 0, yearly: 0 },
  base: { monthly: 290, yearly: 2440 },
  premium: { monthly: 690, yearly: 4900 },
  pro: { monthly: 990, yearly: 8320 },
};

/**
 * Получить требуемый тариф для функции
 * Карта синхронизирована с subscription-access-control.md
 */
export function getRequiredTier(featureId: string): SubscriptionTier {
  const map: Record<string, SubscriptionTier> = {
    // FREE
    'daily': 'free',
    'horoscope_basic': 'free',
    'life_path': 'free',
    'profile_limited': 'free',
    'articles': 'free',
    'natal-chart': 'free',

    // BASE
    'weekly': 'base',
    'monthly': 'base',
    'love': 'base',
    'family': 'base',
    'health': 'base',
    'transits': 'base',
    'progressions': 'base',
    'solar': 'base',
    'lunar': 'base',
    'lunar-returns': 'base',
    'eclipses': 'base',
    'numerology-year': 'base',
    'numerology-month': 'base',
    'numerology-day': 'base',
    'lucky-dates': 'base',
    'synastry': 'base',
    'karmic-compat': 'base',
    'professional-compat': 'base',
    'friendship': 'base',
    'numerology-full': 'base',

    // PREMIUM
    'yearly': 'premium',
    'business': 'premium',
    'karmic': 'premium',
    'past-lives': 'premium',
    'destiny-path': 'premium',
    'electional': 'premium',
    'sexual': 'premium',
    'email-newsletters': 'premium',
    'pdf_export': 'pro',

    // PRO
    'custom': 'pro',
    'business-compat': 'pro',
    'transits-advanced': 'pro',
    'child-profiles': 'pro',
    'telegram-newsletters': 'pro',
    'consult_discount': 'pro',
    'forecast_year': 'pro',
  };
  
  return map[featureId] ?? 'free';
}
